import React, { ReactNode, useState } from "react"
import { Autocomplete, Box, Progress, TextField, Theme, Typography } from "@carrotfertility/carotene-core"
import { useUserContext } from "#/pages/provider-finder/context/UserContext"
import { LabelWithIconTooltip } from "./LabelWithIconTooltip"
// eslint-disable-next-line no-restricted-imports
import { SxProps } from "@mui/system"
import { useIntl } from "react-intl"
import { TypeaheadProvider } from "#/pages/provider-finder/types/Providers"
import {
  renderAutocompleteProviderOption,
  useDebouncedSearch
} from "#/pages/provider-finder/components/keyword-search/keywordSearchUtils"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { useProviderFinderClient } from "#/pages/provider-finder/hooks/useProviderFinderClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"

const TYPEAHEAD_PROVIDERS_QUERY_KEY = "GetTypeaheadProviders"

function useGetTypeaheadProviders(
  providerNameSubstring: string,
  companyCountryCode: string,
  providerType?: string
): UseQueryResult<TypeaheadProvider[], ResponseError> {
  const client = useProviderFinderClient()

  return useQuery<TypeaheadProvider[], ResponseError>(
    [TYPEAHEAD_PROVIDERS_QUERY_KEY, providerNameSubstring, companyCountryCode, providerType],
    () => {
      if (!providerNameSubstring || !companyCountryCode) {
        return null
      }

      return client.getTypeaheadProviders(providerNameSubstring, companyCountryCode, providerType)
    },
    {
      enabled: !!providerNameSubstring,
      refetchOnWindowFocus: false,
      retry: buildCarrotApiRetryPolicy(3)
    }
  )
}

export function KeywordAutocomplete({
  tooltipTitle,
  label,
  keyword,
  popupIcon,
  startAdornment,
  sx,
  setProviderNameValue,
  setTypeaheadLocationSearch,
  providerType,
  placeholder
}: {
  tooltipTitle?: ReactNode
  label?: string
  keyword?: string
  popupIcon: ReactNode
  startAdornment: ReactNode
  sx?: SxProps<Theme>
  setProviderNameValue: (input: string) => void
  setTypeaheadLocationSearch?: (input: string) => void
  providerType?: string
  placeholder?: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [userValue, setUserValue] = useState(null)

  const { companyCountryCode } = useUserContext()

  const {
    inputValue,
    loading: isTyping,
    setInputValue,
    searchValue
  } = useDebouncedSearch(setProviderNameValue, setOpen)

  const { data: providerPredictions, isLoading: isProviderPredictionsLoading } = useGetTypeaheadProviders(
    searchValue,
    companyCountryCode,
    providerType
  )

  const isSearchLoading = isTyping || isProviderPredictionsLoading
  const { isDoulasSearched } = useProviderTypeSearched(providerType)

  const intl = useIntl()

  return (
    <>
      <Autocomplete
        disabled={!providerType}
        id="keyword-search-autocomplete"
        sx={sx}
        autoComplete
        clearOnEscape
        filterOptions={(x) => x}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        loading={isSearchLoading}
        loadingText={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            padding={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
            sx={{ grow: 1 }}
          >
            <Progress />
          </Box>
        }
        noOptionsText={<Typography>{intl.formatMessage({ defaultMessage: "No results found" })}</Typography>}
        options={isSearchLoading ? [] : providerPredictions}
        popupIcon={popupIcon}
        value={keyword || userValue || inputValue}
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        onChange={(event, newUserValue) => {
          setUserValue(newUserValue)
          setProviderNameValue(newUserValue?.name || newUserValue || "")
          if (newUserValue?.address && setTypeaheadLocationSearch) {
            setTypeaheadLocationSearch(newUserValue.address)
          }
          if (newUserValue) {
            setOpen(false)
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "reset") {
            setInputValue(null)
          } else {
            setInputValue(newInputValue)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              label && (
                <LabelWithIconTooltip
                  {...{ label, required: true }}
                  tooltipTitle={isDoulasSearched ? null : tooltipTitle}
                />
              )
            }
            fullWidth
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: startAdornment
            }}
            defaultValue={keyword || null}
          />
        )}
        renderOption={(props, providerPrediction) =>
          renderAutocompleteProviderOption(props, providerPrediction, inputValue)
        }
      />
    </>
  )
}
